import { HttpClient } from '@angular/common/http';
import { API_URL, APP_TOKEN } from '../../environments/environment';
import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { TrainingSectionType } from '../models/training-section.model';
import { VideoInformation } from '../models/video-information.model';
import { Router } from '@angular/router';
import { TrainingModeType } from '../models/training-mode.model';
import { CoachingIntervalType } from '../models/coaching-interval.model';

type ApiResponseType<T> = {
  data: T;
  unlocked_content: any[];
};

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http: HttpClient = inject(HttpClient);
  private router: Router = inject(Router);
  readonly scenicLoadFailed: WritableSignal<boolean> = signal<boolean>(false);
  scenicInformation: Signal<VideoInformation | undefined> = signal<VideoInformation | undefined>(
    undefined,
  );
  scenicTrainingSections: Signal<TrainingSectionType[] | undefined> = signal<
    TrainingSectionType[] | undefined
  >(undefined);
  coachingIntervals: Signal<CoachingIntervalType[] | undefined> = signal<
    CoachingIntervalType[] | undefined
  >(undefined);

  getVideoInformation(
    videoId: number,
    trainingMode: TrainingModeType,
  ): Observable<VideoInformation | undefined> {
    const contentPath: 'coaching' | 'scenic' = trainingMode === 'coaching' ? 'coaching' : 'scenic';
    const url: string = `${API_URL}/${contentPath}/${videoId}?appToken=${APP_TOKEN}`;
    return this.http
      .get<ApiResponseType<VideoInformation>>(url)
      .pipe(map((response: ApiResponseType<VideoInformation>) => response.data))
      .pipe(
        catchError(() => {
          return this.handleScenicLoadError();
        }),
      );
  }

  getScenicInformation(scenicId: number): Observable<VideoInformation | undefined> {
    return this.getVideoInformation(scenicId, 'originalSpeed');
  }

  getSectionAndCoordinates(scenicId: number): Observable<TrainingSectionType[] | undefined> {
    const url: string = `${API_URL}/remote/${scenicId}/sections?appToken=${APP_TOKEN}`;
    return this.http
      .get<ApiResponseType<TrainingSectionType[]>>(url)
      .pipe(map((response: ApiResponseType<TrainingSectionType[]>) => response.data))
      .pipe(
        catchError(() => {
          return this.handleScenicLoadError();
        }),
      );
  }

  getCoachingIntervals(coachingId: number): Observable<CoachingIntervalType[] | undefined> {
    const url: string = `${API_URL}/coaching/${coachingId}/intervals?appToken=${APP_TOKEN}`;
    return this.http
      .get<ApiResponseType<CoachingIntervalType[]>>(url)
      .pipe(map((response: ApiResponseType<CoachingIntervalType[]>) => response.data))
      .pipe(
        catchError(() => {
          return this.handleScenicLoadError();
        }),
      );
  }

  clear(): void {
    this.scenicInformation = signal<VideoInformation | undefined>(undefined);
    this.scenicTrainingSections = signal<TrainingSectionType[] | undefined>(undefined);
    this.coachingIntervals = signal<CoachingIntervalType[] | undefined>(undefined);
    this.scenicLoadFailed.set(false);
  }

  private handleScenicLoadError(): Observable<undefined> {
    this.scenicLoadFailed.set(true);
    this.router.navigate(['connected']);
    return of(undefined);
  }
}
